import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { RootState } from '../../../../store';
import { useAllAvailableDaysOfTheMonth, useAppointmentsAvailable, useGetFeaturedSchedules } from '../../Schedules/hooks';
import { formatTitleByJourneyStatus } from '../utis';
import MobileDatePicker from '../../MobileDatePicker';
import { ISchedules } from '../../../../services/journey.service';
import { EScheduleSteps, updateScheduleSteps } from '../../../../features/schedule/schedule-slice';
import SelectAvailableTimes from '../SelectAvailableTimes';
import { TypeOfAppointment } from '../../../../features/calendar/calendar-slice';
import { IAppointments } from '../../../../services/availableTimes.service';
import { EnumJourneyAction, getJourneyAction } from '../../../../utils/enum/journeyAction';
import { getCurrentSchedule } from '../../JourneyActionManager';
import Modal from '../../Modal/ModalBasic';
import CircularProgress from '@mui/material/CircularProgress';

import { ReturnModalAppointmentsNotFound } from './ModalAppointsmentsNotFound';
import FeaturedAppointments from '../FeaturedAppointments';

const AppointmentsProviders = () => {
  const {
    schedule: {
      ui: { currentStep, previousStep }
    },
    calendar: { typeOfAppointment, calendarsAvailables, journey, schedule }
  } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const PRODUCTS_FEATURED_SCHEDULE_AVAILABLE = process.env.REACT_APP_PRODUCTS_FEATURED_SCHEDULE_AVAILABLE
    ? process.env.REACT_APP_PRODUCTS_FEATURED_SCHEDULE_AVAILABLE.split(',')
    : [];
  const featuredScheduleEnabled = PRODUCTS_FEATURED_SCHEDULE_AVAILABLE.includes(journey.product.id as string);
  const { appointments, setCurrentMonth, setCurrentYear, setAppointments, isLoading, status } = useAppointmentsAvailable();
  const [newAppointments, setNewAppointments] = useState<IAppointments>({
    availableList: [],
    availableListAddress: []
  });
  const [allAvailableDaysOfTheMonth] = useAllAvailableDaysOfTheMonth(newAppointments);
  const featuredScheduleDays = useGetFeaturedSchedules(newAppointments, featuredScheduleEnabled);

  const journeyStatus = getJourneyAction(journey);
  const currentSchedule = schedule ? schedule : (getCurrentSchedule(journey) as ISchedules);

  /**
   * @todo: Refatorar e repensar no nome desse metodo
   */
  const filterOnlyPhysicalAppointments = (prevAppointments: IAppointments): IAppointments => {
    const virtualAppointment = calendarsAvailables?.find(calendar => calendar.type === 'VIRTUAL_CONSULTATION');
    const filteredAppointments: IAppointments = { availableList: [], availableListAddress: [] };

    if (!virtualAppointment) return prevAppointments;
    const virtualId = virtualAppointment.id as string;
    Object.values(prevAppointments.availableListAddress).forEach(calendar => {
      const id = Object.keys(calendar)[0];
      if (id !== virtualId) {
        filteredAppointments.availableList = [...new Set([...filteredAppointments.availableList, ...calendar[id]])];
        filteredAppointments.availableListAddress.push(calendar);
      }
    });
    return filteredAppointments;
  };
  const onlyOne = useRef(true);

  useEffect(() => {
    if (appointments.availableList.length === 0) return;

    if (
      (journeyStatus === EnumJourneyAction.AWAITING_SCHEDULE_RETURN || currentSchedule.returnSchedule === true) &&
      typeOfAppointment === TypeOfAppointment.PHYSICAL_APPOINTMENT
    ) {
      setNewAppointments(filterOnlyPhysicalAppointments(appointments));
    } else {
      setNewAppointments(appointments);
    }
  }, [appointments]);

  useEffect(() => {
    if (currentStep === EScheduleSteps.idle) {
      setAppointments({ availableList: [], availableListAddress: [] });
    }
  }, [currentStep]);

  useEffect(() => {
    if (featuredScheduleDays.length === 0 || previousStep === EScheduleSteps.featuredAppointments) return;

    dispatch(
      updateScheduleSteps({
        currentStep: EScheduleSteps.featuredAppointments,
        previousStep: previousStep
      })
    );
  }, [featuredScheduleDays, currentStep]);

  if (status === QueryStatus.pending && onlyOne.current === true) {
    onlyOne.current = false;
    return (
      <Modal open={true} onCloseHandler={() => console.log('close')} title={'Procurando horários'} typeTitle="h3">
        <CircularProgress sx={{ color: 'brand.darkGreen' }} />
      </Modal>
    );
  }

  if (status === QueryStatus.fulfilled) {
    // Tratativa exclusiva para o retorno
    if (
      appointments.availableList.length === 0 &&
      (journeyStatus === EnumJourneyAction.AWAITING_SCHEDULE_RETURN || currentSchedule.returnSchedule === true) &&
      journey.restrictReturn
    ) {
      return <ReturnModalAppointmentsNotFound schedule={currentSchedule} />;
    }
  }

  if (featuredScheduleEnabled && featuredScheduleDays.length > 0 && currentStep === EScheduleSteps.featuredAppointments) {
    return (
      <FeaturedAppointments
        featuredScheduleDays={featuredScheduleDays}
        appointments={newAppointments}
        open={Boolean(featuredScheduleDays) && currentStep === EScheduleSteps.featuredAppointments}
      />
    );
  }

  if (currentStep === EScheduleSteps.openCalendar) {
    return (
      <MobileDatePicker
        title={formatTitleByJourneyStatus(journey)}
        availableDays={allAvailableDaysOfTheMonth}
        updateYear={setCurrentYear}
        updateMonth={setCurrentMonth}
        isLoading={isLoading}
        onFinished={() => {
          dispatch(updateScheduleSteps({ currentStep: EScheduleSteps.selectTime, previousStep: previousStep }));
        }}
      />
    );
  }

  if (currentStep === EScheduleSteps.selectTime) {
    return (
      <SelectAvailableTimes
        open={currentStep === EScheduleSteps.selectTime ? true : false}
        isLoading={isLoading}
        appointments={appointments}
      />
    );
  }

  return <></>;
};

export default AppointmentsProviders;
